import {
  Container,
  Flex,
  Menu,
  Burger,
  ActionIcon,
  Box,
} from "@mantine/core";
import { NavLinkGNB } from "./NavLinkGNB";
import styled from "styled-components";
import { Avatar } from "@mantine/core";
import logoImage from "../../assets/logo/logo-full.svg";
import logoMinimal from "../../assets/logo/logo-minimal.svg";
import { Image } from "@mantine/core";
import { useLogOut } from "../../utils/auth";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../../hooks/useIsMobile";
import { IconWorld, IconArrowLeft } from "@tabler/icons-react";
import { handleLogin as login } from "../../utils/authUtils";
import { useWritingMode } from "../../contexts/WritingModeContext";
import { SegmentControl } from "../control/SegmentControl";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { ButtonBasic } from "../button/ButtonBasic";

const HeaderWrapper = styled.div`
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  background: ${props => props.backgroundcolor || '#ffffff'}d9;
  
  &::after {
    content: '';
    position: absolute;
    top: 64px;
    left: 0;
    right: 0;
    height: 24px;
    background: ${props => `linear-gradient(to bottom, 
      ${props.backgroundcolor || '#ffffff'}d9 0%,
      ${props.backgroundcolor || '#ffffff'}00 100%)`};
    pointer-events: none;
  }
`;

const HeaderContainer = styled(Container)`
  width: 100%;
  height: 64px;
  padding: 0 24px !important;
  
  @media (max-width: 768px) {
    padding: 0 16px 0px 12px !important;
  }
`;

export const Header = ({
  userData,
  onToggleNav,
  opened,
  isLogin,
  isPostThreadsPage,
}) => {
  const logOut = useLogOut();
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile("xl"); // AppPresenter와 동일한 브레이크포인트
  const navigate = useNavigate();
  const theme = useTheme();

  // WritingMode Context (PostThreads 페이지에서만 사용)
  const { writingMode, switchMode } = useWritingMode();

  // 세그먼트 컨트롤 옵션 (i18n 로케일 적용)
  const modeOptions = [
    { label: t("postThreads.writingModes.preview"), value: "preview" },
    { label: t("postThreads.writingModes.focus"), value: "focus" },
  ];

  const toggleLanguage = (lang) => {
    i18n.changeLanguage(lang);
  };

  const handleLogin = () => {
    login();
  };

  const handleBackClick = () => {
    navigate(-1); // 이전 페이지로 이동
  };

  return (
    <HeaderWrapper backgroundcolor={theme.palette.background.default}>
      <HeaderContainer>
        <Container p={0} style={{ height: "100%" }}>
          <Flex
            size={"100%"}
            justify={"space-between"}
            align={"center"}
            style={{ height: "100%", position: "relative" }}
          >
            {/* PostThreads 페이지일 때의 레이아웃 */}
            {isPostThreadsPage ? (
            <>
              {/* 좌측: 백 버튼 */}
              <ActionIcon
                size="lg"
                variant="subtle"
                radius="xl"
                onClick={handleBackClick}
              >
                <IconArrowLeft size={24} />
              </ActionIcon>

              {/* 가운데: 세그먼트 컨트롤 */}
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                }}
              >
                <SegmentControl
                  options={modeOptions}
                  value={writingMode}
                  onChange={switchMode}
                  size="large"
                />
              </Box>

              {/* 우측: 빈 공간 */}
              <Box sx={{ width: "48px" }} />
            </>
          ) : (
            <>
              {/* 기존 레이아웃 */}
              <Flex align="center" gap="sm">
                {isMobile && isLogin && (
                  <Burger opened={opened} onClick={onToggleNav} size="sm" />
                )}
                {isMobile && !isLogin && (
                  <NavLinkGNB to={"/"}>
                    <Image src={logoMinimal} h={32} w={"auto"} />
                  </NavLinkGNB>
                )}
                {!isMobile && isLogin && (
                  <NavLinkGNB to={"/"}>
                    <Image src={logoImage} h={24} w={"auto"} />
                  </NavLinkGNB>
                )}
                {!isMobile && !isLogin && (
                  <NavLinkGNB to={"/"}>
                    <Image src={logoImage} h={24} w={"auto"} />
                  </NavLinkGNB>
                )}
              </Flex>
              {isMobile && isLogin && (
                <Image
                  src={logoImage}
                  h={16}
                  w={"auto"}
                  style={{
                    position: "absolute",
                    left: "calc(50% - 16px)",
                    transform: "translateX(-50%)",
                  }}
                />
              )}
              <Flex align="center" gap={isMobile ? "xs" : "sm"}>
                <Menu shadow="md" width={100}>
                  <Menu.Target>
                    {isMobile ? (
                      <ActionIcon size="lg" variant="subtle" radius={"xl"}>
                        <IconWorld size={28} />
                      </ActionIcon>
                    ) : (
                      <div>
                        <ButtonBasic size="md" actionType="secondary" radius="full" width="120px" widthType="manual">
                          {i18n.language && i18n.language.includes("ko")
                            ? "한국어"
                            : "Eng"}
                        </ButtonBasic>
                      </div>
                    )}
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item fz={"lg"} onClick={() => toggleLanguage("ko")}>
                      한국어
                    </Menu.Item>
                    <Menu.Item fz={"lg"} onClick={() => toggleLanguage("en")}>
                      English
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
                {!userData && (
                  <ButtonBasic
                    size={isMobile ? "sm" : "md"}
                    radius="full"
                    widthType={isMobile ? "auto" : "manual"}
                    width={isMobile ? undefined : "144px"}
                    actionType="primary"
                    onClick={handleLogin}
                  >
                    {t("landing.getStarted")}
                  </ButtonBasic>
                )}
                {userData && (
                  <Menu shadow="md" width={200}>
                    <Menu.Target>
                      <Avatar
                        size={isMobile ? 32 : 48}
                        src={userData.threads_profile_picture_url}
                        style={{ cursor: "pointer" }}
                      />
                    </Menu.Target>

                    <Menu.Dropdown>
                      <Menu.Label fz={"lg"}>Account</Menu.Label>
                      <Menu.Item fz={"lg"} onClick={logOut}>
                        Logout
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
              </Flex>
            </>
          )}
        </Flex>
      </Container>
    </HeaderContainer>
    </HeaderWrapper>
  );
};
